import React, { useEffect, useState } from "react"
import { Link, useLocation, useParams } from "react-router"
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import Rightbar from './Rightbar'
import { httpOk, GET, FILE_D } from 'helpers/api'
import { formatDate, formatMoney, returnSign, PAGE_SIZES } from 'helpers/helpers'
import { businessToday, businessMonthStart } from 'helpers/businessDay'

function Index() {
	const { t } = useTranslation()
	const { type, id } = useParams()
	const location = useLocation()
	const query = new URLSearchParams(location.search)

	const statuses = [
		{ id: 1, name: t('open') },
		{ id: 10, name: t('closed') },
		{ id: -1, name: t('deleted') },
	]
	const counterpartyTypes = [
		{ id: 'client', name: t('client') },
		{ id: 'employee', name: t('employee') },
		{ id: 'organization', name: t('organization') },
		{ id: 'supplier', name: t('supplier') },
	]

	const [data, setData] = useState({})
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedItem, setSelectedItem] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: query.get('start_date') || businessMonthStart(),
		end_date: query.get('end_date') || businessToday(),
		status: query.get('status') ? Number(query.get('status')) : 10,
		size: 20,
	})
	const [pagination, setPagination] = useState({
		url: `/cheques-by-counterparty/${type}/${id}`,
		size: 20,
		response: null,
	})

	function counterpartyTypeName(value) {
		const item = counterpartyTypes.find(option => option.id === value)
		return item ? item.name : value
	}

	async function toggleRightbar(boolean = false, item, index) {
		const rows = [...(data.data || [])]
		if (boolean) {
			const response = await GET(`/cheques/${item.cheque_id || item.id}`, {}, { loader: true })
			if (httpOk(response)) {
				if (rows[index]) rows[index] = response.data.data
				setSelectedItem(response.data.data)
				setShowRightBar(boolean)
			}
		} else {
			setSelectedItem({})
			setShowRightBar(false)
		}
	}

	function buildFilterParams(filterDataCopy = filterData, includePage = true) {
		let urlParams = ""

		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.status)
			urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
		if (filterDataCopy.size)
			urlParams += returnSign(urlParams) + 'size=' + filterDataCopy.size
		if (includePage && filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		return urlParams
	}

	async function searchWithFilter(nextFilterData = null) {
		const filterDataCopy = { ...(nextFilterData || filterData) }
		let filterUrl = `/cheques-by-counterparty/${type}/${id}`
		let urlParams = buildFilterParams(filterDataCopy)
		checkFilter(filterDataCopy)

		if (`/cheques-by-counterparty/${type}/${id}${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, url: filterUrl, size: filterDataCopy.size, response: response.data })
		}

		setFilterData(filterDataCopy)
		setDropdownIsOpen(false)
	}

	function resetFilter() {
		const nextFilterData = {
			start_date: businessMonthStart(),
			end_date: businessToday(),
			status: 10,
			size: 20,
		}
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	async function downloadExcelFile() {
		let urlParams = buildFilterParams({ ...filterData }, false)
		urlParams += returnSign(urlParams) + 'excel=true'
		const response = await FILE_D(`/cheques-by-counterparty/${type}/${id}${urlParams}`, { loader: true })
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', `cheques-by-counterparty.xlsx`)
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	async function downloadProductsExcelFile() {
		let urlParams = buildFilterParams({ ...filterData }, false)
		urlParams += returnSign(urlParams) + 'excel_products=true'
		const response = await FILE_D(`/cheques-by-counterparty/${type}/${id}${urlParams}`, { loader: true })
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', `cheques-by-counterparty-products.xlsx`)
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	function checkFilter(currentFilterData = filterData) {
		if (
			currentFilterData.start_date === null &&
			currentFilterData.end_date === null &&
			currentFilterData.status === null
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function paginate(page) {
		const nextFilterData = { ...filterData, page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function handlePageSizeChange(size) {
		const nextFilterData = { ...filterData, size, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function returnClassStatus(status) {
		if (status === 10) return "bg-success"
		if (status === 1) return "bg-secondary"
		if (status === -1) return "bg-danger"
		return ""
	}

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const entityName = data?.data?.[0]?.entity_name || `${counterpartyTypeName(type)} #${id}`

	return (
		<>
			<div className="page-header d-flex text-truncate">
				<Link to="/cheques-by-counterparties" className="vertical-center h-100">
					<h4>{t('counterparty_cheques')}</h4>
				</Link>
				<div className="vertical-center">
					<i className="uil uil-angle-double-right fz-20"></i>
				</div>
				<div className="vertical-center" title={id}>
					<h6>{entityName}</h6>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('status')}</label>
												<Select
													isClearable
													options={statuses}
													value={statuses.find(option => option.id === filterData.status) || ''}
													onChange={(option) => setFilterData({ ...filterData, status: option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													withTime={false}
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end">
													<button className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter({ ...filterData, page: null })}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<button className="btn btn-outline-primary btn-wide-2"
									title="Сводный экспорт"
									onClick={() => downloadExcelFile()}>
									<i className="uil uil-file-download-alt"></i>
								</button>
							</div>
							<div>
								<button className="btn btn-outline-success btn-wide-2"
									title="Детализация чеков"
									onClick={() => downloadProductsExcelFile()}>
									<i className="uil uil-list-ul"></i>
								</button>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{(filterData.start_date && filterData.end_date) && <span>|</span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
								{filterData.status &&
									<span className="me-2">
										<b>{t('status')}: </b>
										{statuses.find(option => option.id === filterData.status)?.name}
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover cursor mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th style={{ width: '15%' }}>{t('pos')}</th>
									<th style={{ width: '15%' }}>{t('waiter')}</th>
									<th style={{ width: '15%' }}>{t('cashier')}</th>
									<th style={{ width: '10%' }} className="text-center">{t('cheque')} №</th>
									<th style={{ width: '10%' }} className="text-center">{t('status')}</th>
									<th style={{ width: '15%' }} className="text-center">{t('date')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('return')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('debt')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td className="td-ellipsis" onClick={() => toggleRightbar(true, item, index)}>{item.pos_name}</td>
										<td className="td-ellipsis" onClick={() => toggleRightbar(true, item, index)}>{item.waiter_name}</td>
										<td className="td-ellipsis" onClick={() => toggleRightbar(true, item, index)}>{item.cashier_name}</td>
										<td className="text-center" onClick={() => toggleRightbar(true, item, index)}>{item.cheque_number}</td>
										<td className={`text-center text-white ${returnClassStatus(item.status)}`} onClick={() => toggleRightbar(true, item, index)}>
											{item.status === 10 && t('closed')}
											{item.status === 1 && t('open')}
											{item.status === -1 && t('deleted')}
										</td>
										<td className="text-center" onClick={() => toggleRightbar(true, item, index)}>
											{formatDate(item.cheque_closed_at || item.cheque_opened_at, 'dd.MM.yy HH:mm')}
										</td>
										<td className="text-end" onClick={() => toggleRightbar(true, item, index)}>{formatMoney(item.total_amount_service)}</td>
										<td className="text-end" onClick={() => toggleRightbar(true, item, index)}>{formatMoney(item.total_products, 2)}</td>
										<td className="text-end" onClick={() => toggleRightbar(true, item, index)}>{Number(item.returned_amount) > 0 ? formatMoney(item.returned_amount) : ''}</td>
										<td className="text-end" onClick={() => toggleRightbar(true, item, index)}>{formatMoney(item.entity_debtor_amount)}</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									<td className="text-center text-nowrap"><b>{formatMoney(data.total)}</b></td>
									<td></td>
									<td></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.sales_amount)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_products, 2)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.returned_amount)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_debtor_amount)}</b></td>
								</tr>
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}
							showPageSizeSelect
							pageSizeOptions={PAGE_SIZES}
							onPageSizeChange={handlePageSizeChange}>
						</FilterPagination>
					}
				</div>
			</div>

			{showRightBar &&
				<Rightbar
					showRightBar={showRightBar}
					setShowRightBar={setShowRightBar}
					selectedItem={selectedItem}>
				</Rightbar>
			}
		</>
	)
}

export default Index
